/// <amd-module name="Artifacts/Package/components/UpstreamVersionsTable" />

import * as Res from "Artifacts/Package/Resources";
import { resizeColumn } from "Artifacts/Root/helpers/TableHelper";
import * as RootRes from "Artifacts/Root/Resources";
import {
  UpstreamVersionsData,
  UpstreamVersionsDataVersionUpstream,
} from "Artifacts/Root/types/PackagingShared";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { Card } from "VSSUI/Card";
import { Pill } from "VSSUI/Pill";
import { PillGroup, PillGroupOverflow } from "VSSUI/PillGroup";
import { ColumnMore, ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { getSafeId } from "VSSUI/Util";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

interface IUpstreamVersionsTableItem {
  displayVersion: string;
  normalizedVersion: string;
  isLocal: boolean;
  isDeleted: boolean;
  upstreams: UpstreamVersionsDataVersionUpstream[];
  isIngestable: boolean;
}

export interface IUpstreamVersionsTableProps {
  upstreamData: UpstreamVersionsData;
  showIngestionModal: (normalizedVersion: string) => void;
  showUpstreamSourceDetailsPanel: (
    upstreams: UpstreamVersionsDataVersionUpstream[],
    normalizedVersion: string,
    ingestIsDisabled: boolean,
    versionIsDeleted: boolean,
  ) => void;
  supportedProtocolForIngestion: boolean;
}

const UpstreamVersionsTable: React.FC<IUpstreamVersionsTableProps> = ({
  upstreamData,
  showIngestionModal,
  showUpstreamSourceDetailsPanel,
  supportedProtocolForIngestion,
}) => {
  const getUpstreamDetailsCommand = (item: IUpstreamVersionsTableItem) => {
    return {
      id: "upstream-details",
      onActivate: () => {
        showUpstreamSourceDetailsPanel(
          item.upstreams,
          item.normalizedVersion,
          !item.isIngestable,
          item.isDeleted,
        );
      },
      text: Res.UpstreamVersionsTable_UpstreamDetails,
    };
  };
  let columns = [
    {
      id: "version",
      name: Res.PackageDetails_VersionsTable_Version,
      readonly: true,
      renderCell: renderVersion,
      width: new ObservableValue(-50),
      onSize: (event, index, width) => {
        columns = resizeColumn(columns, index, width);
      },
    } as ITableColumn<IUpstreamVersionsTableItem>,
    {
      id: "upstreams",
      name: Res.UpstreamVersionsTable_UpstreamsColumn,
      readonly: true,
      renderCell: renderUpstreams,
      width: new ObservableValue(-100),
      onSize: (event, index, width) => {
        columns = resizeColumn(columns, index, width);
      },
    } as ITableColumn<IUpstreamVersionsTableItem>,
    new ColumnMore<IUpstreamVersionsTableItem>((item) => {
      return {
        id: "sub-menu",
        items: supportedProtocolForIngestion
          ? [
              {
                id: "ingest",
                onActivate: () => {
                  showIngestionModal(item.normalizedVersion);
                },
                text: Res.UpstreamVersionsTable_IngestButton,
                disabled: !item.isIngestable,
              },
              getUpstreamDetailsCommand(item),
            ]
          : [getUpstreamDetailsCommand(item)],
      };
    }),
  ];

  const tableItems = React.useMemo(() => {
    const items = upstreamData.versions.map<IUpstreamVersionsTableItem>((pkgversion) => ({
      displayVersion: pkgversion.displayVersion,
      isLocal: pkgversion.isLocal,
      isDeleted: pkgversion.localInstance?.isDeleted,
      upstreams: pkgversion.upstreams,
      normalizedVersion: pkgversion.normalizedVersion,
      isIngestable:
        !pkgversion.isLocal &&
        upstreamData.userHasPermissionToIngestFromUpstream &&
        !!pkgversion.selectedUpstreamId,
    }));
    return new ArrayItemProvider<IUpstreamVersionsTableItem | undefined>(items);
  }, [upstreamData]);

  const ariaMessageId = "search-upstreams-aria-description";

  return (
    <div className="page-content page-content-top">
      <Card
        className="flex-grow bolt-table-card upstream-version-list-card"
        contentProps={{ contentPadding: false }}
      >
        <div className="visibility-hidden" id={getSafeId(ariaMessageId)}>
          {RootRes.PackageListSelection_ScreenreaderHelp}
        </div>
        <section
          aria-label={Res.PackageVersionTable_PackageList}
          aria-describedby={getSafeId(ariaMessageId)}
        >
          <Table columns={columns} itemProvider={tableItems} />
        </section>
      </Card>
    </div>
  );
};

function renderUpstreams(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IUpstreamVersionsTableItem>,
  tableItem: IUpstreamVersionsTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <PillGroup className="flex-row" overflow={PillGroupOverflow.wrap}>
        {tableItem.upstreams.map((upstream) => (
          <Pill key={upstream.upstreamId} excludeFocusZone={true}>
            {upstream.upstreamName}
          </Pill>
        ))}
      </PillGroup>
    </SimpleTableCell>
  );
}

function renderVersion(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IUpstreamVersionsTableItem>,
  tableItem: IUpstreamVersionsTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <div className={"flex-row"}>
        <div className="version-number">{tableItem.displayVersion}</div>
        {tableItem.isLocal ? (
          <Pill excludeFocusZone={true}>
            {tableItem.isDeleted ? Res.SearchUpstreamsLocalDeleted : Res.SearchUpstreamsLocalPill}
          </Pill>
        ) : null}
      </div>
    </SimpleTableCell>
  );
}

export { UpstreamVersionsTable };
